<script setup lang="ts">
import type { LandingResponse } from '@/types/landing'
import { inject } from 'vue'
import LocationIcon from '@/assets/images/location-icon.svg?component'
import logoImg from '@/assets/images/logo.png'
import MailIcon from '@/assets/images/mail-icon.svg?component'
import maxLogo from '@/assets/images/max-logo.png'
import PhoneIcon from '@/assets/images/phone-icon.svg?component'
import UiContainer from '@/components/landing/ui/UiContainer.vue'

const currentYear = new Date().getFullYear()
const contentData = inject<LandingResponse | null>('contentData')

const pages = [
  { name: 'Синдром красного глаза', to: '/red-eye' },
  { name: 'Сетчатка', to: '/retina' },
  { name: 'Диагностика и лечение глаукомы', to: '/glaucoma' },
  { name: 'Миопия', to: '/myopia' },
  { name: 'Документы', to: '/documents' },
]
</script>

<template>
  <footer class="the-footer">
    <UiContainer>
      <div class="the-footer__grid">
        <div class="the-footer__logo-col">
          <RouterLink to="/">
            <img :src="logoImg" alt="Ключ к диагнозу" class="the-footer__logo">
          </RouterLink>
        </div>
        <nav class="the-footer__pages">
          <RouterLink v-for="page in pages" :key="page.to" :to="page.to" class="the-footer__page-link">
            {{ page.name }}
          </RouterLink>
        </nav>

        <div class="the-footer__contacts">
          <a :href="contentData?.contacts.maxLinks[0] || '#'" class="link the-footer__contact-item">
            <img :src="maxLogo" alt="MAX" class="the-footer__max-logo">
            <span class="the-footer__contact-text">MAX</span>
          </a>
          <a :href="`tel:${contentData?.contacts.phones[0]}`" class="link the-footer__contact-item">
            <PhoneIcon class="the-footer__contact-icon" />
            <span class="the-footer__contact-text">{{ contentData?.contacts.phones[0] }}</span>
          </a>
          <div v-if="contentData?.contacts.emails" class="the-footer__contact-item the-footer__contact-item--email">
            <MailIcon class="the-footer__contact-icon" />
            <div class="the-footer__email-block">
              <span class="the-footer__email-label">Электронная почта</span>
              <a
                v-for="email of contentData?.contacts.emails" :key="email" :href="`mailto:${email}`"
                class="link the-footer__email-link"
              >{{ email }}</a>
            </div>
          </div>
        </div>

        <div v-if="contentData?.contacts.addresses" class="the-footer__address-col">
          <div class="the-footer__contact-item link">
            <LocationIcon class="the-footer__contact-icon" />
            <div v-for="address of contentData?.contacts.addresses" :key="address" class="the-footer__address-block">
              <a href="https://yandex.by/maps/-/CPgEaG8t" target="_blank" class="the-footer__contact-text">{{ address
              }}</a>
            </div>
          </div>
        </div>
      </div>
    </UiContainer>

    <div class="the-footer__bottom">
      <UiContainer>
        <div class="the-footer__bottom-inner">
          <p>&copy; {{ currentYear }} Ключ к диагнозу. Все права защищены.</p>
          <a href="https://softwarecenter.ru/" target="_blank" class="the-footer__dev-link">SWC</a>
        </div>
      </UiContainer>
    </div>
  </footer>
</template>

<style scoped lang="scss">
.the-footer {
  background-color: $color-bg-light;
  border-top: 1px solid $color-border;

  &__grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: $spacing-2xl;
    padding: $spacing-2xl 0;

    @include md {
      grid-template-columns: auto 1fr 1fr 1fr;
      gap: $spacing-xl;
    }
  }

  &__logo {
    height: 5.5rem;
    width: auto;
    mix-blend-mode: multiply;

    @media (max-width: $breakpoint-sm) {
      height: 4rem;
    }
  }

  &__pages {
    display: flex;
    flex-direction: column;
    gap: $spacing-md;
  }

  &__page-link {
    font-size: $font-size-xl;
    font-weight: $font-weight-medium;
    color: $color-text;
    transition: color $transition-fast;

    &:hover {
      color: $color-primary !important;
    }
  }

  &__contacts {
    display: flex;
    flex-direction: column;
    gap: $spacing-md;
  }

  &__contact-item {
    display: flex;
    align-items: center;
    gap: $spacing-sm;

    &--email {
      align-items: flex-start;
    }

    &.link {
      &:hover {
        .the-footer__contact-text {
          color: $color-primary !important;
        }
      }
    }
  }

  &__max-logo {
    width: 2.25rem;
    height: 2.25rem;
    border-radius: $radius-full;
    object-fit: cover;
  }

  &__contact-icon {
    width: 1.5rem;
    height: 1.5rem;
    flex-shrink: 0;
  }

  &__contact-text {
    font-size: $font-size-xl;
    font-weight: $font-weight-medium;
    color: $color-text;
    transition: color $transition-fast;
  }

  &__email-block {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__email-label {
    font-size: $font-size-xl;
    font-weight: $font-weight-medium;
    color: $color-primary-dark;
  }

  &__email-link {
    font-size: $font-size-xl;
    font-weight: $font-weight-medium;
    color: $color-text;
    text-decoration: underline;

    @media (max-width: $breakpoint-sm) {
      font-size: $font-size-base;
      word-break: break-all;
    }
  }

  &__address-col {
    display: flex;
    flex-direction: column;
    gap: $spacing-md;
  }

  &__address-block {
    display: flex;
    flex-direction: column;
    cursor: pointer;
  }

  &__address-city {
    font-size: $font-size-xl;
    font-weight: $font-weight-semibold;
    color: $color-primary-dark;
  }

  &__bottom {
    border-top: 1px solid $color-border;
    padding: $spacing-lg 0;
  }

  &__bottom-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: $font-size-xl;
    font-weight: $font-weight-medium;
    color: $color-text;
    gap: $spacing-md;

    @media (max-width: $breakpoint-sm) {
      flex-direction: column;
      text-align: center;
      font-size: $font-size-base;
    }
  }

  &__dev-link {
    color: $color-primary-dark;
    text-decoration: underline;
    transition: opacity $transition-fast;

    &:hover {
      opacity: .7 !important;
    }
  }
}
</style>
